import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { redeemShopifyConnectId } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export default function ShopifyConnectPage() {
  const { user, loading: authLoading, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const connectId = searchParams.get("code") || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [error, setError] = useState("");

  // Persist connectId across registration / email verification / first login.
  useEffect(() => {
    if (!connectId) return;
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("radar_shopify_connect_id", connectId);
    } catch {
      // Ignore storage errors (e.g. private mode).
    }
  }, [connectId]);

  const canRedeem = useMemo(() => Boolean(connectId) && Boolean(user), [connectId, user]);

  const redeem = useCallback(async () => {
    if (!connectId || !user || redeemSuccess || redeeming) return;

    setRedeeming(true);
    setError("");

    try {
      const maxAttempts = 12;
      const waitMs = 800;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const data = await redeemShopifyConnectId(connectId);
          if (data?.success) {
            setRedeemSuccess(true);
            return;
          }
          setError(data?.message || data?.error || "Failed to link store");
          return;
        } catch (e) {
          const apiError = e?.response?.data;
          const code = apiError?.error;

          // If Shopify submitted the connect-id mapping but it hasn't landed in DB yet,
          // retry for a short window.
          if (code === "unknown_connect_id" && attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, waitMs));
            continue;
          }

          setError(e?.message || apiError?.message || "Failed to link store");
          return;
        }
      }
    } catch (e) {
      setError(e?.message || "Failed to link store");
    } finally {
      setRedeeming(false);
    }
  }, [connectId, user, redeemSuccess, redeeming]);

  useEffect(() => {
    if (authLoading) return;
    if (!canRedeem) return;
    // Shopify creates the connect mapping first; give the backend a brief moment
    // to persist it before attempting redemption.
    const t = setTimeout(() => redeem(), 1200);
    return () => clearTimeout(t);
  }, [authLoading, canRedeem, redeem]);

  if (!connectId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Missing connect ID</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>Connect ID is required to link your store.</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (redeemSuccess) {
    // Clear pending connect-id once linking has succeeded.
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem("radar_shopify_connect_id");
      } catch {
        // ignore
      }
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Store Linked</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>Your Shopify store is now linked to your Radar account.</AlertDescription>
            </Alert>
            <div className="text-sm text-muted-foreground">
              You can go back to the Shopify app and refresh Settings to see the updated status.
            </div>
            <Button onClick={() => (window.location.href = "/dashboard")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign in to link your store</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Password</label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" />
            </div>
            <Button
              onClick={async () => {
                setError("");
                try {
                  await signIn({ email, password });
                } catch (e) {
                  setError(e?.message || "Login failed");
                }
              }}
              className="w-full"
              disabled={redeeming || !email || !password}
            >
              {redeeming ? "Linking..." : "Sign in & Link"}
            </Button>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>By signing in, you will link the store to the Radar account you choose.</div>
              <div>
                New to Radar?{" "}
                <a href="/register" className="text-primary underline">
                  Create an account
                </a>{" "}
                and then sign in again to link this store.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is logged in, but redemption hasn't succeeded yet.
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Could not link store</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="text-xs text-muted-foreground">
              If this is a 404, it usually means the connect ID was not registered on the backend.
              Go back to the Shopify app Settings, generate a new Connect ID, and try again.
            </div>
            <Button
              onClick={() => {
                setError("");
                setRedeemSuccess(false);
                redeem();
              }}
              className="w-full"
              disabled={redeeming}
            >
              {redeeming ? "Retrying..." : "Try again"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="flex items-center gap-3">
        <Loader2 className="animate-spin" />
        <div>Linking your store...</div>
      </div>
    </div>
  );
}

